import { ADD_SPLIT_NODE, REMOVE_SPLIT_NODE } from 'domains/campaigns/state/actionTypes';
import {
    ABTestConfig,
    CampaignChannels,
    DirectMailSendInputState,
    EmailSendInputState,
    ExitCriteriaFields,
    ReEntryFields,
    SendRecurrenceFrequency,
    SmsSendInputState,
    SplitActions,
    SplitTypes,
    VoiceCallSendInput,
} from 'domains/campaigns/types';
import { calculateComparisonFields } from 'domains/campaigns/utils';
import {
    EngagementAPIOperator,
    EngagementUIOperator,
    SegmentFilterCategorySpecificField,
    SegmentFilterState,
} from 'domains/segments/types';
import { engagementUiToApiOperator } from 'domains/segments/utils';
import { CampaignType, FilterCategory } from 'models/enums';

import { actionTypes } from './customCampaignActions';

export type CampaignSend = DirectMailSendInputState | EmailSendInputState | SmsSendInputState | VoiceCallSendInput;

export type CampaignStateTypes = {
    name: string;
    type: CampaignType;
    sends: CampaignSend[];
    reentryDetails?: ReEntryFields;
    exitCriteria?: ExitCriteriaFields | null;
    abTestConfig?: ABTestConfig;
    // True when this campaign was started from the "Mix" entry point: every send's channel is
    // chosen by picking its content (not declared up front), and there is no fixed channel order.
    isMixedChannel?: boolean;
};

export const directMailInitialState: CampaignStateTypes = {
    name: '',
    type: CampaignType.FLEXIBLE,
    sends: [
        {
            channel: CampaignChannels.DIRECT_MAIL,
            cohortId: '',
            content: {
                id: null,
                name: '',
            },
            name: '',
            recurrenceFrequency: SendRecurrenceFrequency.DOES_NOT_REPEAT,
            scheduledEndTimestamp: null,
            segmentId: null,
            scheduledTimestamp: null,
            senderProfileId: null,
        },
    ],
    reentryDetails: null,
    exitCriteria: null,
};

export const emailInitialState: CampaignStateTypes = {
    name: '',
    type: CampaignType.FLEXIBLE,
    sends: [
        {
            channel: CampaignChannels.EMAIL,
            cohortId: '',
            name: '',
            preHeader: '',
            recurrenceFrequency: SendRecurrenceFrequency.DOES_NOT_REPEAT,
            scheduledEndTimestamp: null,
            scheduledTimestamp: null,
            segmentId: null,
            senderProfileId: null,
            subjectLine: '',
            templateId: null,
        },
    ],
    reentryDetails: null,
    abTestConfig: null,
};

export const smsInitialState: CampaignStateTypes = {
    name: '',
    type: CampaignType.FLEXIBLE,
    sends: [
        {
            channel: CampaignChannels.SMS,
            cohortId: '',
            content: {
                id: null,
                name: '',
            },
            name: '',
            recurrenceFrequency: SendRecurrenceFrequency.DOES_NOT_REPEAT,
            scheduledEndTimestamp: null,
            scheduledTimestamp: null,
            segmentId: null,
            smsSenderProfileId: null,
        },
    ],
    reentryDetails: null,
    exitCriteria: null,
};

export const voiceCallInitialState: CampaignStateTypes = {
    name: '',
    type: CampaignType.FLEXIBLE,
    sends: [
        {
            callerProfileId: null,
            channel: CampaignChannels.VOICE_CALL,
            cohortId: '',
            content: {
                id: null,
                name: '',
            },
            name: '',
            recurrenceFrequency: SendRecurrenceFrequency.DOES_NOT_REPEAT,
            scheduledTimestamp: null,
            segmentId: null,
        },
    ],
    reentryDetails: null,
    exitCriteria: null,
};

const additionalEmailSend: EmailSendInputState = {
    channel: CampaignChannels.EMAIL,
    name: '',
    parentIndex: null,
    preHeader: '',
    senderProfileId: null,
    subjectLine: '',
    templateId: null,
};

const additionalSmsSend: SmsSendInputState = {
    channel: CampaignChannels.SMS,
    content: { id: null, name: '' },
    name: '',
    scheduledTimestamp: null,
    smsSenderProfileId: null,
};

const additionalDirectMailSend: DirectMailSendInputState = {
    channel: CampaignChannels.DIRECT_MAIL,
    content: { id: null, name: '' },
    name: '',
    recurrenceFrequency: SendRecurrenceFrequency.DOES_NOT_REPEAT,
    scheduledEndTimestamp: null,
    segmentId: null,
    senderProfileId: null,
};

const additionalVoiceSend: {
    channel: CampaignChannels.VOICE_CALL;
    callerProfileId: number | null;
    content: { id: number | null; name: string };
    name: string;
} = {
    channel: CampaignChannels.VOICE_CALL,
    callerProfileId: null,
    content: { id: null, name: '' },
    name: '',
};

/**
 * Builds a follow-up send in its own channel's shape. Anything not listed falls back to email.
 */
const buildAdditionalSend = (channel: CampaignChannels): CampaignSend => {
    if (channel === CampaignChannels.SMS) return { ...additionalSmsSend };
    if (channel === CampaignChannels.DIRECT_MAIL) return { ...additionalDirectMailSend };
    if (channel === CampaignChannels.VOICE_CALL) return { ...additionalVoiceSend } as CampaignSend;
    return { ...additionalEmailSend };
};

const emailContentFieldsReset: Partial<EmailSendInputState> = {
    templateId: null,
    senderProfileId: null,
    subjectLine: '',
    preHeader: '',
    name: '',
};

function getUpdatedSends(sends: CampaignStateTypes['sends'], activeSendIndex: number, updater: any) {
    return sends.map((send, index) => {
        if (index === activeSendIndex) return { ...send, ...updater };
        return send;
    });
}

/**
 * Finds the DOES_NOT_MEET sibling of the MEETS send at `meetsIndex`. Never assume the sibling
 * sits at `meetsIndex + 1` — ADD_BRANCH_SEND inserts a MEETS-branch send (e.g. a time-delay send)
 * before it to keep nav ordering correct, which shifts it further down the array. Siblings created
 * via ADD_SPLIT_NODE share a `splitNodeId`; legacy root splits (no `splitNodeId`) share `parentIndex`.
 */
function findDoesNotMeetSiblingIndex(sends: CampaignStateTypes['sends'], meetsIndex: number): number {
    const meetsSend = sends[meetsIndex] as any;
    return sends.findIndex(
        (send: any, idx: number) =>
            idx !== meetsIndex &&
            send.splitType === SplitTypes.DOES_NOT_MEET &&
            (meetsSend?.splitNodeId != null
                ? send.splitNodeId === meetsSend.splitNodeId
                : send.parentIndex === meetsSend?.parentIndex)
    );
}

function getUpdatedSendsWithSegments(
    sends: CampaignStateTypes['sends'],
    meetsCriteriaSegment: SegmentFilterState<SegmentFilterCategorySpecificField>,
    activeSendIndex: number
) {
    const { category } = meetsCriteriaSegment;
    const doesNotMeetIndex = findDoesNotMeetSiblingIndex(sends, activeSendIndex);

    if (category === FilterCategory.CONTACT_ACTIVITY) {
        const sendsWithMeetsCriteriaSegments = getUpdatedSends(sends, activeSendIndex, {
            segments: [meetsCriteriaSegment],
        });

        const doesNotMeetCriteriaOperator = Object.values(EngagementAPIOperator).filter(
            (operator) =>
                operator !==
                engagementUiToApiOperator[(meetsCriteriaSegment.operator as unknown) as EngagementUIOperator]
        )[0];
        const doesNotMeetCriteriaSegment = {
            ...meetsCriteriaSegment,
            operator: doesNotMeetCriteriaOperator,
        };

        const sendWithSegments = getUpdatedSends(sendsWithMeetsCriteriaSegments, doesNotMeetIndex, {
            segments: [doesNotMeetCriteriaSegment],
        });

        return sendWithSegments;
    }

    if (
        category === FilterCategory.CONTACT_DETAILS ||
        category === FilterCategory.CONTACT_EVENTS ||
        category === FilterCategory.CONTACT_OBJECTS
    ) {
        const sendsWithMeetsCriteriaSegments = getUpdatedSends(sends, activeSendIndex, {
            segments: [meetsCriteriaSegment],
        });

        // While the campaign is being built out, the segments for meets and
        // does not meet criteria will be the same. We will tell the backend
        // to negate the query associated with the does not meet criteria segment
        // upon campaign publish.
        const doesNotMeetCriteriaSegment = meetsCriteriaSegment;

        const sendWithSegments = getUpdatedSends(sendsWithMeetsCriteriaSegments, doesNotMeetIndex, {
            segments: [doesNotMeetCriteriaSegment],
        });

        return sendWithSegments;
    }
}

/**
 * Helper function to update comparison fields in AB test config
 * Only updates if AB testing is enabled and there are two email sends
 */
function updateABTestComparisonFields(state: CampaignStateTypes): CampaignStateTypes {
    // Only proceed if AB testing is enabled
    if (!state.abTestConfig || !state.abTestConfig.testOn) {
        return state;
    }

    // Only calculate for content AB testing with email sends
    if (state.abTestConfig.testOn !== 'content') {
        return state;
    }

    // Make sure we have exactly 2 email sends
    if (
        state.sends.length !== 2 ||
        state.sends[0].channel !== CampaignChannels.EMAIL ||
        state.sends[1].channel !== CampaignChannels.EMAIL
    ) {
        return state;
    }

    // Calculate comparison fields
    const comparisonFields = calculateComparisonFields(
        state.sends[0] as EmailSendInputState,
        state.sends[1] as EmailSendInputState
    );

    // Update abTestConfig with comparisonFields
    return {
        ...state,
        abTestConfig: {
            ...state.abTestConfig,
            comparisonFields,
        },
    };
}

export function customCurationReducer(state: CampaignStateTypes, action: any) {
    switch (action.type) {
        case actionTypes.SET_NAME: {
            return {
                ...state,
                name: action.name,
            };
        }
        case actionTypes.SELECT_AUDIENCE: {
            return {
                ...state,
                sends: [
                    {
                        ...state.sends[0],
                        segmentId: action.selectedAudienceId,
                    },
                    ...state.sends.slice(1),
                ],
            };
        }
        case actionTypes.SET_COHORT_ID: {
            const { cohortId } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, 0, { cohortId });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_DIRECT_MAIL_CONTENT: {
            const { contentId, contentName, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                content: { id: contentId, name: contentName },
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_VOICE_CALL_CONTENT: {
            const { contentId, contentName, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                content: { id: contentId, name: contentName },
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_SMS_CONTENT: {
            const { contentId, contentName, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                content: { id: contentId, name: contentName },
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_TEMPLATE_ID: {
            const { templateId, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { templateId });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_SENDER_PROFILE_ID: {
            const { sendIndex, senderProfileId } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { senderProfileId });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_SUBJECT_LINE: {
            const { sendIndex, subjectLine } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { subjectLine });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_PRE_HEADER: {
            const { preHeader, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { preHeader });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_SEND_NAME: {
            const { name, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { name });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_SCHEDULED_TIMESTAMP: {
            const { scheduledTimestamp, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { scheduledTimestamp });
            const newState = { ...state, sends: updatedSends };

            return updateABTestComparisonFields(newState);
        }
        case actionTypes.SET_HAS_RECOMMENDED_TIME: {
            const { hasRecommendedTime, sendIndex = 0 } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { hasRecommendedTime });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_RECURRENCE_FREQUENCY: {
            const { recurrenceFrequency } = action.parameters;
            const monthlyFields =
                recurrenceFrequency !== SendRecurrenceFrequency.MONTHLY
                    ? { recurrenceDayOfWeek: null, recurrenceOccurrencePosition: null }
                    : {};
            const updatedSends = getUpdatedSends(state.sends, 0, { recurrenceFrequency, ...monthlyFields });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_RECURRENCE_DAY_OF_WEEK: {
            const { recurrenceDayOfWeek } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, 0, { recurrenceDayOfWeek });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_RECURRENCE_OCCURRENCE_POSITION: {
            const { recurrenceOccurrencePosition } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, 0, { recurrenceOccurrencePosition });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_SCHEDULED_END_TIMESTAMP: {
            const { scheduledEndTimestamp } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, 0, { scheduledEndTimestamp });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_PARENT_AUDIENCE_DELAY_INTERVAL: {
            const { parentAudienceDelayInterval, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { parentAudienceDelayInterval });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.ADD_CONDITIONAL_SPLIT_SENDS: {
            const { sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                splitType: SplitTypes.MEETS,
                ...emailContentFieldsReset,
            });

            const meetsCriteriaSend = state.sends[sendIndex];
            const doesNotMeetCriteriaSend = {
                ...meetsCriteriaSend,
                ...emailContentFieldsReset,
                splitType: SplitTypes.DOES_NOT_MEET,
            };

            const sendsWithConditionalSplit = updatedSends.concat(doesNotMeetCriteriaSend);
            return { ...state, sends: sendsWithConditionalSplit };
        }
        case actionTypes.ADD_RANDOM_SPLIT_SENDS: {
            const { parentIndex } = action.parameters;
            const parentSend = state.sends[parentIndex] as any;

            // If the parent is a blank delay placeholder (delay set, no content, no split type),
            // collapse it: point Group A/B directly at the grandparent and inherit the delay.
            const isBlankDelaySend =
                parentSend?.parentAudienceDelayInterval && !parentSend?.templateId && !parentSend?.splitType;
            const effectiveParentIndex = isBlankDelaySend ? parentSend.parentIndex ?? 0 : parentIndex;
            const inheritedDelay: string | undefined = isBlankDelaySend
                ? parentSend.parentAudienceDelayInterval
                : undefined;

            const groupASend: EmailSendInputState = {
                ...additionalEmailSend,
                parentIndex: effectiveParentIndex,
                splitType: SplitTypes.RANDOM_GROUP_1,
                splitAction: SplitActions.SEND,
                ...(inheritedDelay && { parentAudienceDelayInterval: inheritedDelay }),
            };
            const groupBSend: EmailSendInputState = {
                ...additionalEmailSend,
                parentIndex: effectiveParentIndex,
                splitType: SplitTypes.RANDOM_GROUP_2,
                splitAction: SplitActions.SEND,
                ...(inheritedDelay && { parentAudienceDelayInterval: inheritedDelay }),
            };

            if (isBlankDelaySend) {
                // Remove the blank delay send and re-index any parentIndex references above it.
                const filteredSends = state.sends.filter((_: any, idx: number) => idx !== parentIndex);
                const reindexedSends = filteredSends.map((send: any) => {
                    if (send.parentIndex != null && send.parentIndex > parentIndex) {
                        return { ...send, parentIndex: send.parentIndex - 1 };
                    }
                    return send;
                });
                return { ...state, sends: [...reindexedSends, groupASend, groupBSend] };
            }

            return { ...state, sends: [...state.sends, groupASend, groupBSend] };
        }
        case actionTypes.REMOVE_RANDOM_SPLIT: {
            const { parentIndex } = action.parameters;

            const indicesToRemove = new Set<number>();
            const collectDescendants = (pIdx: number) => {
                state.sends.forEach((send: any, idx: number) => {
                    if (send.parentIndex === pIdx && !indicesToRemove.has(idx)) {
                        indicesToRemove.add(idx);
                        collectDescendants(idx);
                    }
                });
            };

            // Collect both random group children and their descendants
            state.sends.forEach((send: any, idx: number) => {
                if (
                    (send.splitType === SplitTypes.RANDOM_GROUP_1 || send.splitType === SplitTypes.RANDOM_GROUP_2) &&
                    (send.parentIndex ?? 0) === parentIndex
                ) {
                    indicesToRemove.add(idx);
                    collectDescendants(idx);
                }
            });

            const indexMapping = new Map<number, number>();
            let newIdx = 0;
            state.sends.forEach((_: any, oldIdx: number) => {
                if (!indicesToRemove.has(oldIdx)) {
                    indexMapping.set(oldIdx, newIdx);
                    newIdx++;
                }
            });

            const newSends = state.sends
                .map((send: any, idx: number) => ({ send, originalIdx: idx }))
                .filter(({ originalIdx }) => !indicesToRemove.has(originalIdx))
                .map(({ send }) => {
                    if (send.parentIndex != null) {
                        const mapped = indexMapping.get(send.parentIndex);
                        if (mapped !== undefined) return { ...send, parentIndex: mapped };
                    }
                    return send;
                });

            return { ...state, sends: newSends };
        }
        case actionTypes.SET_SEGMENT: {
            const { segment: meetsCriteriaSegment, sendIndex } = action.parameters;

            const sendsWithSegments = getUpdatedSendsWithSegments(state.sends, meetsCriteriaSegment, sendIndex);

            return { ...state, sends: sendsWithSegments };
        }
        case actionTypes.SET_SPLIT_SEGMENT_DEFINITION: {
            const { sendIndex, segmentDefinition } = action.parameters;
            const doesNotMeetIndex = findDoesNotMeetSiblingIndex(state.sends, sendIndex);

            const sendsWithMeetsDefinition = getUpdatedSends(state.sends, sendIndex, { segmentDefinition });
            const doesNotMeetDefinition = { ...segmentDefinition, negateQuery: !segmentDefinition.negateQuery };
            const sendsWithBothDefinitions = getUpdatedSends(sendsWithMeetsDefinition, doesNotMeetIndex, {
                segmentDefinition: doesNotMeetDefinition,
            });

            return { ...state, sends: sendsWithBothDefinitions };
        }
        case actionTypes.SET_SPLIT_ACTION: {
            const { splitAction, sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { splitAction });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.ADD_SEND: {
            const addedSend = {
                ...buildAdditionalSend(action.parameters.channel),
                parentIndex: action.parameters.parentIndex,
            };

            const updatedSends = [...state.sends, addedSend];
            return { ...state, sends: updatedSends };
        }
        case actionTypes.ADD_BRANCH_SEND: {
            const { parentIndex, splitType, channel } = action.parameters;
            const addedSend = {
                ...buildAdditionalSend(channel),
                parentIndex,
                splitType,
                splitAction: SplitActions.SEND,
            };

            const insertBeforeSplitType =
                splitType === SplitTypes.MEETS
                    ? SplitTypes.DOES_NOT_MEET
                    : splitType === SplitTypes.RANDOM_GROUP_1
                    ? SplitTypes.RANDOM_GROUP_2
                    : null;

            if (insertBeforeSplitType) {
                let rootParentIdx = parentIndex;
                let current = state.sends[parentIndex] as any;
                while (current?.splitType === splitType && current?.parentIndex != null) {
                    rootParentIdx = current.parentIndex;
                    current = state.sends[rootParentIdx] as any;
                }

                const insertionIndex = state.sends.findIndex(
                    (send: any) => send.splitType === insertBeforeSplitType && send.parentIndex === rootParentIdx
                );

                if (insertionIndex !== -1) {
                    const updatedSends = [
                        ...state.sends.slice(0, insertionIndex),
                        addedSend,
                        ...state.sends.slice(insertionIndex),
                    ];

                    const adjustedSends = updatedSends.map((send: any, index: number) => {
                        if (index === insertionIndex) return send;
                        if (
                            send.parentIndex !== undefined &&
                            send.parentIndex !== null &&
                            send.parentIndex >= insertionIndex
                        ) {
                            return { ...send, parentIndex: send.parentIndex + 1 };
                        }
                        return send;
                    });

                    return { ...state, sends: adjustedSends };
                }
            }

            const updatedSends = [...state.sends, addedSend];
            return { ...state, sends: updatedSends };
        }
        case actionTypes.REMOVE_SEND: {
            const updatedSends = state.sends.slice(0, -1);
            return { ...state, sends: updatedSends };
        }
        case actionTypes.REMOVE_BRANCH_DESCENDANTS: {
            const { sendIndex } = action.parameters;

            const descendantIndices = new Set<number>();
            const collectDescendants = (parentIdx: number) => {
                state.sends.forEach((send: any, idx: number) => {
                    if (send.parentIndex === parentIdx && !descendantIndices.has(idx)) {
                        descendantIndices.add(idx);
                        collectDescendants(idx);
                    }
                });
            };
            collectDescendants(sendIndex);

            if (descendantIndices.size === 0) return state;

            const indexMapping = new Map<number, number>();
            let newIdx = 0;
            state.sends.forEach((_: any, oldIdx: number) => {
                if (!descendantIndices.has(oldIdx)) {
                    indexMapping.set(oldIdx, newIdx);
                    newIdx++;
                }
            });

            const filteredSends = state.sends
                .filter((_: any, idx: number) => !descendantIndices.has(idx))
                .map((send: any) => {
                    if (send.parentIndex != null) {
                        const mapped = indexMapping.get(send.parentIndex);
                        if (mapped !== undefined) return { ...send, parentIndex: mapped };
                    }
                    return send;
                });

            return { ...state, sends: filteredSends };
        }
        case actionTypes.REMOVE_CONDITIONAL_SPLIT: {
            const { sendIndex } = action.parameters;
            const activeSend = state.sends[sendIndex] as any;
            const activeParentIndex = activeSend?.parentIndex ?? 0;

            const dnmIndex = state.sends.findIndex(
                (send: any, idx: number) =>
                    idx !== sendIndex &&
                    send.splitType === SplitTypes.DOES_NOT_MEET &&
                    (send.parentIndex ?? 0) === activeParentIndex
            );

            const indicesToRemove = new Set<number>();
            if (dnmIndex !== -1) indicesToRemove.add(dnmIndex);

            const collectDescendants = (parentIdx: number) => {
                state.sends.forEach((send: any, idx: number) => {
                    if (send.parentIndex === parentIdx && !indicesToRemove.has(idx)) {
                        indicesToRemove.add(idx);
                        collectDescendants(idx);
                    }
                });
            };
            collectDescendants(sendIndex);
            if (dnmIndex !== -1) collectDescendants(dnmIndex);

            const indexMapping = new Map<number, number>();
            let newIdx = 0;
            state.sends.forEach((_: any, oldIdx: number) => {
                if (!indicesToRemove.has(oldIdx)) {
                    indexMapping.set(oldIdx, newIdx);
                    newIdx++;
                }
            });

            const newSends = state.sends
                .map((send: any, idx: number) => ({ send, originalIdx: idx }))
                .filter(({ originalIdx }) => !indicesToRemove.has(originalIdx))
                .map(({ send, originalIdx }) => {
                    let updatedSend = { ...send };

                    if (updatedSend.parentIndex != null) {
                        const mapped = indexMapping.get(updatedSend.parentIndex);
                        if (mapped !== undefined) updatedSend.parentIndex = mapped;
                    }

                    if (originalIdx === sendIndex) {
                        const { splitType, splitAction, segments, ...cleaned } = updatedSend;
                        return { ...cleaned, ...emailContentFieldsReset };
                    }

                    return updatedSend;
                });

            return { ...state, sends: newSends };
        }
        case actionTypes.RESET_CONDITIONAL_SPLIT_SEND: {
            const { sendIndex } = action.parameters;
            const existingSend = state.sends[sendIndex] as any;
            // Channel-aware: this used to unconditionally merge Email-shaped fields
            // (subjectLine/templateId/senderProfileId) onto whatever send this ran on. For an
            // SMS/Direct Mail branch that already had real content configured, that silently
            // bolted stray Email fields onto it alongside its actual content — a mixed, corrupt
            // shape — instead of clearing that channel's own content.
            const channelReset: Record<string, any> =
                existingSend?.channel === CampaignChannels.SMS
                    ? { content: { id: null, name: '' }, smsSenderProfileId: null }
                    : existingSend?.channel === CampaignChannels.DIRECT_MAIL
                    ? { content: { id: null, name: '' }, senderProfileId: null }
                    : { senderProfileId: null, subjectLine: '', preHeader: '', templateId: null };
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                scheduledTimestamp: null,
                ...channelReset,
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.RESET_CONTENT: {
            const { sendIndex } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, {
                content: {
                    id: null,
                    name: '',
                },
            });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_RE_ENTRY_DETAILS: {
            return {
                ...state,
                reentryDetails: action.parameters.reentryDetails,
            };
        }
        case actionTypes.CLEAR_RE_ENTRY_DETAILS: {
            return {
                ...state,
                reentryDetails: null,
            };
        }
        case actionTypes.SET_EXIT_CRITERIA: {
            return {
                ...state,
                exitCriteria: action.parameters.exitCriteria,
            };
        }
        case actionTypes.CLEAR_EXIT_CRITERIA: {
            return {
                ...state,
                exitCriteria: null,
            };
        }
        case actionTypes.SET_CALLER_PROFILE_ID: {
            const { sendIndex, callerProfileId } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { callerProfileId });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_SMS_SENDER_PROFILE_ID: {
            const { sendIndex, smsSenderProfileId } = action.parameters;
            const updatedSends = getUpdatedSends(state.sends, sendIndex, { smsSenderProfileId });

            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_AB_TEST_CONFIG: {
            const { abTestConfig } = action.parameters;

            return { ...state, abTestConfig };
        }
        case actionTypes.SET_AB_TEST_PERCENTAGE: {
            const { testPercentage } = action.parameters;

            return {
                ...state,
                abTestConfig: {
                    ...state.abTestConfig,
                    testPercentage,
                },
            };
        }
        case actionTypes.UPDATE_COMPARISON_FIELDS: {
            return updateABTestComparisonFields(state);
        }
        case actionTypes.REMOVE_AB_TEST_CONFIG: {
            const { abTestConfig, ...stateWithoutABTestConfig } = state;

            return stateWithoutABTestConfig;
        }
        case actionTypes.RESET_SENDS: {
            const currentChannel = state.sends[0]?.channel;
            const currentAudienceData = {
                segmentId: state.sends[0]?.segmentId,
                cohortId: state.sends[0]?.cohortId,
            };

            let initialSends;

            switch (currentChannel) {
                case CampaignChannels.EMAIL:
                    initialSends = emailInitialState.sends;
                    break;
                case CampaignChannels.SMS:
                    initialSends = smsInitialState.sends;
                    break;
                case CampaignChannels.DIRECT_MAIL:
                    initialSends = directMailInitialState.sends;
                    break;
                case CampaignChannels.VOICE_CALL:
                    initialSends = voiceCallInitialState.sends;
                    break;
                default:
                    initialSends = state.sends; // fallback to current state
            }

            // Reset to initial state but preserve audience selection
            const resetSends = [...initialSends];
            if (resetSends[0]) {
                resetSends[0] = {
                    ...resetSends[0],
                    ...currentAudienceData,
                };
            }

            return { ...state, sends: resetSends };
        }
        case actionTypes.SET_SEND_CHANNEL: {
            const { sendIndex, channel } = action.parameters;
            const existingSend = state.sends[sendIndex] as any;

            const rebuiltSend = {
                ...buildAdditionalSend(channel),
                name: existingSend.name,
                parentIndex: existingSend.parentIndex,
                ...(existingSend.splitType !== undefined && { splitType: existingSend.splitType }),
                ...(existingSend.splitAction !== undefined && { splitAction: existingSend.splitAction }),
                ...(existingSend.splitNodeId !== undefined && { splitNodeId: existingSend.splitNodeId }),
                // Split criteria must survive a channel rebuild: in a mixed-channel curation the
                // user sets criteria BEFORE picking content (which triggers SET_SEND_CHANNEL), so
                // omitting these would silently clear the criteria and block publish.
                ...(existingSend.segmentDefinition !== undefined && {
                    segmentDefinition: existingSend.segmentDefinition,
                }),
                ...(existingSend.segments !== undefined && { segments: existingSend.segments }),
                // The root send (no parentIndex) carries the campaign's audience directly —
                // child sends inherit it server-side via parentId, but the root's own
                // segmentId/cohortId must survive a channel rebuild or publish fails validation.
                ...(existingSend.segmentId !== undefined && { segmentId: existingSend.segmentId }),
                ...(existingSend.cohortId !== undefined && { cohortId: existingSend.cohortId }),
                // The time-delay step sets parentAudienceDelayInterval on this send *before* its
                // content/channel is picked, so a rebuild here must preserve it or the delay the
                // user configured silently disappears from the published campaign.
                ...(existingSend.parentAudienceDelayInterval !== undefined && {
                    parentAudienceDelayInterval: existingSend.parentAudienceDelayInterval,
                }),
                // Recurrence settings survive a channel rebuild so the schedule step doesn't show
                // a blank dropdown. emailInitialState seeds DOES_NOT_REPEAT on sends[0]; after
                // SET_SEND_CHANNEL the rebuilt send comes from additionalEmailSend/additionalSmsSend
                // which have no recurrenceFrequency, causing getRecurrenceLabel to return ''.
                ...(existingSend.recurrenceFrequency !== undefined && {
                    recurrenceFrequency: existingSend.recurrenceFrequency,
                }),
                ...(existingSend.recurrenceDayOfWeek !== undefined && {
                    recurrenceDayOfWeek: existingSend.recurrenceDayOfWeek,
                }),
                ...(existingSend.recurrenceOccurrencePosition !== undefined && {
                    recurrenceOccurrencePosition: existingSend.recurrenceOccurrencePosition,
                }),
            };

            const updatedSends = state.sends.map((send, index) => (index === sendIndex ? rebuiltSend : send));
            return { ...state, sends: updatedSends };
        }
        case actionTypes.SET_DRAFT_STATE: {
            // Action to set the entire state from draft data
            return action.draftState;
        }
        case ADD_SPLIT_NODE: {
            const { splitNodeId, parentSendIndex, parentAudienceDelayInterval } = action.payload;
            const meetsSend: EmailSendInputState = {
                ...additionalEmailSend,
                parentIndex: parentSendIndex,
                splitType: SplitTypes.MEETS,
                splitAction: SplitActions.SEND,
                splitNodeId,
                ...(parentAudienceDelayInterval && { parentAudienceDelayInterval }),
            };
            const dnmSend: EmailSendInputState = {
                ...additionalEmailSend,
                parentIndex: parentSendIndex,
                splitType: SplitTypes.DOES_NOT_MEET,
                splitAction: SplitActions.SEND,
                splitNodeId,
                ...(parentAudienceDelayInterval && { parentAudienceDelayInterval }),
            };
            return { ...state, sends: [...state.sends, meetsSend, dnmSend] };
        }
        case REMOVE_SPLIT_NODE: {
            const { splitNodeId } = action.payload;

            // Collect indices of all sends belonging to this split node
            const indicesToRemove = new Set<number>();
            state.sends.forEach((send: any, idx: number) => {
                if (send.splitNodeId === splitNodeId) indicesToRemove.add(idx);
            });

            if (indicesToRemove.size === 0) return state;

            // Recursively collect descendants by parentIndex
            const collectDescendants = (parentIdx: number) => {
                state.sends.forEach((send: any, idx: number) => {
                    if (send.parentIndex === parentIdx && !indicesToRemove.has(idx)) {
                        indicesToRemove.add(idx);
                        collectDescendants(idx);
                    }
                });
            };
            indicesToRemove.forEach((idx) => collectDescendants(idx));

            // Build a re-index map so parentIndex references stay valid
            const indexMapping = new Map<number, number>();
            let newIdx = 0;
            state.sends.forEach((_: any, oldIdx: number) => {
                if (!indicesToRemove.has(oldIdx)) {
                    indexMapping.set(oldIdx, newIdx);
                    newIdx++;
                }
            });

            const newSends = state.sends
                .filter((_: any, idx: number) => !indicesToRemove.has(idx))
                .map((send: any) => {
                    if (send.parentIndex != null) {
                        const mapped = indexMapping.get(send.parentIndex);
                        if (mapped !== undefined) return { ...send, parentIndex: mapped };
                    }
                    return send;
                });

            return { ...state, sends: newSends };
        }
    }

    throw Error('Unknown action: ' + action.type);
}
